/**
 * Wires Event Registration state through the unified <CheckoutShell>.
 * Renders voucher / discount-code / admin-discount controls, LATA +
 * waitlist banners, and the free-checkout path — all driven by the
 * shared shell + cart-metadata flags. Events don't own bespoke checkout
 * UI; the adapter is the integration layer between RegistrationContext
 * and the shell.
 *
 * The adapter is the seam between the event domain and the shell. It:
 *   1. Reads RegistrationContext state.
 *   2. Calls buildEventCart() to produce a Cart in the shell's contract.
 *   3. Wires interactive recompute callbacks (apply voucher, code, admin
 *      discount) to existing context mutators; each callback returns a
 *      fresh Cart by re-running buildEventCart with the new state.
 *   4. Wires onSubmit to completeEventRegistration. Mid-flight
 *      availability changes are caught here and shown via a domain-specific
 *      dialog — the shell never knows about that concern.
 *   5. Slots LATA + waitlist + attendee summary banners and the
 *      "View enrollment" success link.
 */

import React, { ReactNode, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Users } from "lucide-react";

import { CheckoutShell } from "@/modules/checkout/components/CheckoutShell";
import type { Cart, SubmitInput, SubmitResult } from "@/modules/checkout/types";
import permissionsService from "@/services/permissionsService";

import { useRegistration } from "../../context/RegistrationContext";
import { buildEventCart } from "../../utils/buildEventCart";
import { buildCompleteRegistrationPayload } from "../../utils/buildCompleteRegistrationPayload";
import { generateInvoiceNumber } from "@/modules/checkout/utils/generateInvoiceNumber";
import { completeEventRegistration } from "../../api/completeEventRegistration/completeEventRegistrationApi";

const ADMIN_PERMISSION = "administration-events";

export default function EventCheckoutShellAdapter() {
  const navigate = useNavigate();
  const { hasPermission } = permissionsService();

  const {
    eventInfo,
    eventSessionUuid,
    contactName,
    contactEmail,
    contactPhone,
    groupNotes,
    attendees,
    voucherQuantity,
    discountCode,
    discountValueType,
    discountRawValue,
    adminDiscountType,
    adminDiscountValue,
    adminDiscountReason,
    applyVoucher,
    removeVoucher,
    applyDiscountCode,
    removeDiscountCode,
    applyAdminDiscount,
    removeAdminDiscount,
  } = useRegistration();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const adminCanDiscount = hasPermission(ADMIN_PERMISSION);

  // Recompute the cart synchronously on every render. Cheap because
  // buildEventCart is a pure function over current state. The shell owns
  // its own copy via initial prop + cart-update callbacks; this is the
  // *initial* shape passed in.
  const initialCart: Cart = useMemo(
    () =>
      eventInfo
        ? buildEventCart({
            eventInfo,
            attendees,
            voucherQuantity,
            discountCode,
            discountValueType,
            discountRawValue,
            adminDiscountType,
            adminDiscountValue,
            adminDiscountReason,
            adminCanDiscount,
          })
        : ({
            lines: [],
            discountLines: [],
            subtotal: 0,
            total: 0,
            needsShipping: false,
            acceptsDiscountCode: false,
            acceptsVouchers: false,
            adminCanDiscount: false,
          } as Cart),
    [eventInfo, attendees],
  );

  const recomputeCart = (overrides: {
    voucherQuantity?: number;
    discountCode?: string | null;
    discountValueType?: "percentage" | "fixed_amount" | null;
    discountRawValue?: number;
    adminDiscountType?: "percentage" | "fixed_amount" | null;
    adminDiscountValue?: number;
    adminDiscountReason?: string;
  }): Cart => {
    if (!eventInfo) return initialCart;
    return buildEventCart({
      eventInfo,
      attendees,
      voucherQuantity: overrides.voucherQuantity ?? voucherQuantity,
      discountCode: overrides.discountCode ?? discountCode,
      discountValueType: overrides.discountValueType ?? discountValueType,
      discountRawValue: overrides.discountRawValue ?? discountRawValue,
      adminDiscountType: overrides.adminDiscountType ?? adminDiscountType,
      adminDiscountValue: overrides.adminDiscountValue ?? adminDiscountValue,
      adminDiscountReason: overrides.adminDiscountReason ?? adminDiscountReason,
      adminCanDiscount,
    });
  };

  const onApplyVouchers = async (count: number): Promise<Cart> => {
    if (count <= 0) {
      removeVoucher();
      return recomputeCart({ voucherQuantity: 0 });
    }
    applyVoucher(count);
    return recomputeCart({ voucherQuantity: count });
  };

  // Hard ceiling on voucher count: one voucher per paid attendee, and
  // never more than the company's available voucher seats. The
  // VoucherSelector enforces it on the input; the BE is the final
  // authority at submit.
  const paidAttendeeCount = attendees.filter(
    (a) => a.isSelected && !a.isWaitlist,
  ).length;
  const voucherMaxQuantity = Math.min(
    paidAttendeeCount,
    eventInfo?.companyAvailableVoucherSeats ?? 0,
  );

  const onApplyDiscountCode = async (code: string): Promise<Cart> => {
    // Phase 2 stays purely local: events V2 already has discount-code
    // metadata on eventInfo.discounts. Look up the matching code and
    // apply it. Server-side re-validation happens at submit time.
    const match = (eventInfo?.discounts ?? []).find(
      (d) => d.code?.toLowerCase() === code.toLowerCase(),
    );
    if (!match || !match.discountType || !match.discountValue) {
      throw new Error("Code not recognized.");
    }
    const valueType =
      match.discountType === "percentage" ? "percentage" : "fixed_amount";
    const rawValue = parseFloat(match.discountValue);
    applyDiscountCode(match.code ?? code, valueType, rawValue);
    return recomputeCart({
      discountCode: match.code ?? code,
      discountValueType: valueType,
      discountRawValue: rawValue,
    });
  };

  const onClearDiscountCode = async (): Promise<Cart> => {
    removeDiscountCode();
    return recomputeCart({
      discountCode: null,
      discountValueType: null,
      discountRawValue: 0,
    });
  };

  const onApplyAdminDiscount = async (input: {
    type: "percentage" | "fixed_amount";
    value: number;
    reason: string;
  }): Promise<Cart> => {
    applyAdminDiscount(input.type, input.value, input.reason);
    return recomputeCart({
      adminDiscountType: input.type,
      adminDiscountValue: input.value,
      adminDiscountReason: input.reason,
    });
  };

  const onClearAdminDiscount = async (): Promise<Cart> => {
    removeAdminDiscount();
    return recomputeCart({
      adminDiscountType: null,
      adminDiscountValue: 0,
      adminDiscountReason: "",
    });
  };

  const onSubmit = async (input: SubmitInput): Promise<SubmitResult> => {
    if (!eventInfo) {
      throw new Error("Event info missing — cannot submit.");
    }

    const invoiceNumber = generateInvoiceNumber();
    const isFree = input.cart.total === 0;

    const dataDescriptor =
      input.payment?.kind === "token"
        ? input.payment.token.dataDescriptor
        : "FREE";
    const dataValue =
      input.payment?.kind === "token" ? input.payment.token.dataValue : "FREE";
    const cardExpiration =
      input.payment?.kind === "token"
        ? input.payment.token.cardExpiration
        : undefined;

    const payload = buildCompleteRegistrationPayload({
      eventSessionUuid,
      contactName,
      contactEmail,
      contactPhone,
      groupNotes,
      attendees,
      dataDescriptor,
      dataValue,
      amount: isFree ? 0 : input.cart.total,
      invoiceNumber,
      cardExpiration,
      voucherQuantity,
      discountCode,
      discountAmount:
        input.cart.discountLines
          .filter(
            (d) =>
              d.source === "discount_code" || d.source === "admin_discount",
          )
          .reduce((sum, d) => sum + d.amount, 0) || 0,
      adminDiscountType,
      adminDiscountValue,
      adminDiscountReason,
    });

    const response = await completeEventRegistration(payload);

    if (!response.data.success || !response.data.eventCheckoutUuid) {
      throw new Error("Registration could not be completed.");
    }

    const successPath = `/event-registration/events/${response.data.eventCheckoutUuid}/confirmation`;
    navigate(successPath);
    return {
      confirmationNumber:
        response.data.confirmationNumber ?? response.data.transactionId ?? "—",
      successPath,
    };
  };

  if (!eventInfo || attendees.length === 0) return null;

  const selectedCount = attendees.filter((a) => a.isSelected).length;
  const waitlistedCount = attendees.filter(
    (a) => a.isSelected && a.isWaitlist,
  ).length;
  const isCoveredByTotalAccess = eventInfo.isCoveredByTotalAccess ?? false;

  const aboveCart: ReactNode = (
    <div className="space-y-3 mb-6">
      {isCoveredByTotalAccess ? (
        <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Included with your Total Access subscription — no charge for this
            event.
          </p>
        </div>
      ) : null}
      {waitlistedCount > 0 ? (
        <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-700 dark:text-amber-300 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            {waitlistedCount === selectedCount
              ? "All attendees will be waitlisted — no charge."
              : `${waitlistedCount} of ${selectedCount} attendees will be waitlisted (no charge).`}
          </p>
        </div>
      ) : null}
      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <Users className="h-4 w-4" />
        <span>
          {selectedCount} attendee{selectedCount === 1 ? "" : "s"} selected
        </span>
      </div>
    </div>
  );

  return (
    <>
      <CheckoutShell
        cart={initialCart}
        onApplyAdminDiscount={onApplyAdminDiscount}
        onApplyDiscountCode={onApplyDiscountCode}
        onApplyVouchers={onApplyVouchers}
        onClearAdminDiscount={onClearAdminDiscount}
        onClearDiscountCode={onClearDiscountCode}
        onSubmit={onSubmit}
        paymentMode="inline"
        slots={{ aboveCart }}
        voucherMaxQuantity={voucherMaxQuantity}
      />
    </>
  );
}
